import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAuthTracking } from '../../hooks/useAnalytics';
import { logger } from '../../utils/logger';
import PageHeader from '../common/PageHeader';
import { useAnalytics } from '../../hooks/useAnalytics';

/**
 * SignIn component for email and password authentication
 */
const SignIn: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState<boolean>(false);

  const { signIn, signInWithGooglePopup, currentUser, hasUsername, loading: authLoading, profileLoading } = useAuth();
  const { trackLoginEvent } = useAuthTracking();
  const { trackPageView, trackCustomEvent } = useAnalytics();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the returnUrl from the query parameters
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get('returnUrl');

  // Track page view when component mounts
  useEffect(() => {
    trackPageView({
      page_title: 'Sign In',
      page_location: window.location.href,
      page_referrer: document.referrer
    });
    
    // Track sign-in page view with context
    trackCustomEvent('signin_page_viewed', {
      has_return_url: !!returnUrl,
      return_url: returnUrl || 'none'
    });
  }, [trackPageView, trackCustomEvent, returnUrl]);  useEffect(() => {
    // First, check if we need to redirect at all
    if (!currentUser || authLoading || profileLoading) {
      // Still loading or not logged in, don't redirect yet
      return;
    }
    
    // At this point, we're logged in and all data is loaded, so we can make decisions
    
    // If user doesn't have a username, redirect to username selection page
    if (!hasUsername) {
      logger.info('👤 User redirected to username selection');
      
      // Track redirection to username selection
      trackCustomEvent('signin_redirect', {
        destination: 'choose_username',
        has_username: false
      });
      
      navigate('/choose-username');
    } 
    // If there's a return URL, navigate there
    else if (returnUrl) {
      logger.info(`👤 User redirected to return URL: ${returnUrl}`);
      
      // Track redirection to return URL
      trackCustomEvent('signin_redirect', {
        destination: 'return_url',
        has_username: true,
        return_url: returnUrl
      });
      
      navigate(returnUrl);
    } 
    // Otherwise, go to profile
    else {
      logger.info('👤 User redirected to profile page');
      
      // Track redirection to profile
      trackCustomEvent('signin_redirect', {
        destination: 'my_profile',
        has_username: true
      });
      
      navigate('/my-profile');
    }
  }, [currentUser, hasUsername, navigate, returnUrl, trackCustomEvent, authLoading, profileLoading]);

  // Handle email/password sign in
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!email || !password) {
      setError('Please enter both email and password');
      
      // Track validation error
      trackCustomEvent('signin_validation_error', {
        error_type: !email ? 'missing_email' : 'missing_password'
      });
      
      return;
    }    try {
      setError('');
      setIsSubmitting(true);
      
      // Track sign-in attempt
      trackCustomEvent('signin_attempt', {
        method: 'email',
        email_domain: email.split('@')[1]
      });
      
      // Sign in with provided credentials
      await signIn(email, password);
      
      // Track successful login
      trackLoginEvent('email');
      
      // If successful, we redirect in the useEffect handler
      // that monitors the currentUser and hasUsername status
      logger.info('✅ User signed in successfully');
    } catch (err: any) {
      logger.error('❌ Sign in error', err);

      // Track sign-in error
      trackCustomEvent('signin_error', {
        error_code: err.code || 'unknown',
        error_message: err.message || 'Unknown error'
      });

      // Handle specific Firebase Auth errors with user-friendly messages
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later.');
      } else if (err.code === 'auth/user-disabled') {
        setError('This account has been disabled. Please contact support.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email format');
      } else if (err.code === 'auth/email-not-verified') {
        setShowVerificationMessage(true);
        setError('Please verify your email before signing in');
      } else {
        setError('Failed to sign in. Please try again.');
      }    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Google sign-in
  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setIsSubmitting(true);
      logger.info('🔄 Starting Google sign-in process (popup)');
      
      // Track Google sign-in attempt
      trackCustomEvent('signin_attempt', {
        method: 'google'
      });
      
      await signInWithGooglePopup();
      
      // Track successful Google login
      trackLoginEvent('google');
      
      // On success, user is redirected to /my-profile by AuthContext
    } catch (err: any) {
      logger.error('❌ Google sign-in initiation error', err);
      
      // Track Google sign-in error
      trackCustomEvent('signin_error', {
        method: 'google',
        error_code: err.code || 'unknown',
        error_message: err.message || 'Unknown error'
      });      
      setError('Failed to start Google sign-in. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Members Sign In"
        subtitle="Log in as a member into our community"
      />
      <div className="sign-in-container">
        <h2 className="text-center">Sign In</h2>

        {error && <div className="auth-error">{error}</div>}

        {showVerificationMessage && (
          <div className="verification-message">
            Please check your email to verify your account before signing in.
            <button onClick={() => setShowVerificationMessage(false)} className="dismiss-btn">
              Dismiss
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
              placeholder="Enter your email"
              autoComplete="username email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isSubmitting}
              placeholder="Enter your password"
              autoComplete="current-password"
            />
          </div>          <button 
            type="submit"
            className="primary-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="divider">OR</div>        <button 
          onClick={handleGoogleSignIn}
          className="google-button"
          disabled={isSubmitting}
        >
          Sign In with Google
        </button>

        <div className="auth-links">
          <Link 
            to="/reset-password"
            onClick={() => trackCustomEvent('navigation_click', {
              source: 'signin_page',
              destination: 'reset_password'
            })}
          >
            Forgot Password?
          </Link>
          <span className="separator">|</span>
          <Link 
            to="/sign-up"
            onClick={() => trackCustomEvent('navigation_click', {
              source: 'signin_page',
              destination: 'signup'
            })}
          >
            Need an account? Sign Up
          </Link>
        </div>
      </div>
    </>
  );
};

export default SignIn;
